import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import api from '../services/api';
import { Search, Edit2, Trash2, Plus, Upload, Download, Loader, AlertCircle, X, Filter, RefreshCw, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Eye } from 'lucide-react';
import EditCustomerModal from '../components/EditCustomerModal';
import AddCustomerModal from '../components/AddCustomerModal';
import NewInstallationModal from '../components/NewInstallationModal';
import InstallationInvoiceView from '../components/InstallationInvoiceView';
import EditInstallationInvoiceModal from '../components/EditInstallationInvoiceModal';
import ImportCustomersModal from '../components/ImportCustomersModal';
import { useDebounce } from '../hooks/useDebounce';

const PAGE_SIZE_OPTIONS = [50, 100, 200];

const Customers = () => {
  const navigate = useNavigate();
  const abortControllerRef = useRef(null);
  const [customers, setCustomers] = useState([]);
  const [allPackages, setAllPackages] = useState([]);
  const [allAreas, setAllAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchingFilters, setFetchingFilters] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);

  // Pagination state
  // Load saved filters from localStorage synchronously
  const savedFiltersRaw = (() => { try { return JSON.parse(localStorage.getItem('opticflow_customer_filters') || '{}'); } catch { return {}; } })();

  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(savedFiltersRaw.pageSize || 50);
  const [nextUrl, setNextUrl] = useState(null);
  const [prevUrl, setPrevUrl] = useState(null);

  // Search & Filter State
  const [searchTerm, setSearchTerm] = useState(savedFiltersRaw.searchTerm || '');
  const [filters, setFilters] = useState(savedFiltersRaw.filters || { status: '', package: '', area: '' });

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Modal States
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isInstallModalOpen, setIsInstallModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(null);

  const handleViewInvoice = async (customerId) => {
    try {
      const res = await api.get(`/installation-invoices/by-customer/${customerId}/`);
      setViewingInvoice(res.data);
    } catch (err) {
      alert('No installation invoice found for this customer.');
    }
  };

  // Save filters to localStorage when they change
  useEffect(() => {
    const filterData = { searchTerm, filters, pageSize };
    localStorage.setItem('opticflow_customer_filters', JSON.stringify(filterData));
  }, [searchTerm, filters, pageSize]);

  // Fetch customers — server-side pagination + filtering
  const fetchCustomers = useCallback(async (page = 1) => {
    // Cancel previous request to prevent race conditions
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      params.set('page', page);
      params.set('page_size', pageSize);
      if (debouncedSearchTerm) params.set('search', debouncedSearchTerm);
      if (filters.status) params.set('status', filters.status);
      if (filters.package) params.set('package', filters.package);
      if (filters.area) params.set('area', filters.area);

      const res = await api.get(`/customers/?${params.toString()}`, {
        signal: abortControllerRef.current.signal
      });

      setCustomers(res.data.results || []);
      setTotalCount(res.data.count || 0);
      setNextUrl(res.data.next);
      setPrevUrl(res.data.previous);
      setCurrentPage(page);
    } catch (err) {
      // Don't show error if request was cancelled
      if (err.code !== 'ERR_CANCELED') {
        console.error("Error fetching customers:", err);
        setError("Failed to load customers. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }, [debouncedSearchTerm, filters.status, filters.package, filters.area, pageSize]);

  // Fetch filter dropdown options (packages, areas) once
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const [pkgRes, areaRes] = await Promise.all([
          api.get('/packages/'),
          api.get('/areas/'),
        ]);
        setAllPackages(pkgRes.data.results || []);
        setAllAreas(areaRes.data.results || []);
      } catch (err) {
        console.error("Error fetching filter options:", err);
      } finally {
        setFetchingFilters(false);
      }
    };
    fetchFilterOptions();
  }, []);

  // Re-fetch customers when filters/search/pageSize change
  useEffect(() => {
    fetchCustomers(1); // Reset to page 1 when filters change
  }, [fetchCustomers]);

  // Cleanup: abort requests on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/customers/${id}/`);
      setDeleteConfirm(null);
      fetchCustomers(currentPage);
    } catch (error) {
      console.error("Error deleting customer:", error);
      alert("Failed to delete customer.");
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilters({ status: '', package: '', area: '' });
    localStorage.removeItem('opticflow_customer_filters');
  };

  const formatDate = (value) => value ? new Date(value).toLocaleDateString('en-PK') : 'N/A';

  const formatAmount = (value) => {
    const amount = Number(value || 0);
    return `PKR ${amount.toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getSelectedFilterSummary = () => {
    const selectedPackage = allPackages.find(p => p.id === parseInt(filters.package));
    const selectedArea = allAreas.find(a => a.id === parseInt(filters.area));

    return [
      ['Search', debouncedSearchTerm || 'All'],
      ['Status', filters.status || 'All'],
      ['Package', selectedPackage?.name || 'All'],
      ['Area', selectedArea?.name || 'All'],
    ];
  };

  const downloadCustomersPdf = (exportCustomers) => {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const exportedAt = new Date().toLocaleString('en-PK', {
      year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
    const filterSummary = getSelectedFilterSummary();

    doc.setFontSize(18);
    doc.setTextColor(37, 99, 235);
    doc.text('Optic Flow Customers Export', 40, 40);
    doc.setFontSize(10);
    doc.setTextColor(75, 85, 99);
    doc.text(`Exported: ${exportedAt}`, 40, 58);
    doc.text(`Total: ${exportCustomers.length.toLocaleString('en-PK')} customers`, 40, 74);

    const filterText = filterSummary.map(([label, value]) => `${label}: ${value}`).join('  |  ');
    doc.setTextColor(17, 24, 39);
    doc.text(filterText, 40, 96, { maxWidth: 760 });

    autoTable(doc, {
      startY: 116,
      head: [[
        '#', 'Username', 'Name', 'Phone', 'Package', 'Area', 'Status', 'Expiry', 'Monthly'
      ]],
      body: exportCustomers.length > 0 ? exportCustomers.map((customer, index) => [
        index + 1,
        customer.username || 'N/A',
        customer.name || 'N/A',
        customer.phone || 'N/A',
        customer.package_name || 'N/A',
        customer.area_name || 'N/A',
        (customer.status || 'N/A').toUpperCase(),
        formatDate(customer.expiry_date),
        formatAmount(customer.effective_price ?? customer.package_monthly_price),
      ]) : [['', 'No customers found for the selected filters.', '', '', '', '', '', '', '']],
      styles: { fontSize: 8, cellPadding: 4, overflow: 'linebreak' },
      headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [249, 250, 251] },
      columnStyles: {
        0: { cellWidth: 28 },
        8: { halign: 'right' },
      },
      margin: { left: 40, right: 40 },
    });

    const dateStamp = new Date().toISOString().slice(0, 10);
    doc.save(`optic-flow-customers-${dateStamp}.pdf`);
  };

  const handleExportPdf = async () => {
    try {
      setExporting(true);

      const params = new URLSearchParams();
      params.set('page', 1);
      params.set('page_size', totalCount || pageSize);
      if (debouncedSearchTerm) params.set('search', debouncedSearchTerm);
      if (filters.status) params.set('status', filters.status);
      if (filters.package) params.set('package', filters.package);
      if (filters.area) params.set('area', filters.area);

      const res = totalCount > 0 ? await api.get(`/customers/?${params.toString()}`) : { data: { results: [] } };
      downloadCustomersPdf(res.data.results || []);
    } catch (err) {
      console.error('Error exporting customers:', err);
      alert('Failed to export customers. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const hasActiveFilters = searchTerm || filters.status || filters.package || filters.area;

  const totalPages = Math.ceil(totalCount / pageSize);
  const startItem = totalCount > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      fetchCustomers(page);
    }
  };

  if (loading && customers.length === 0) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Loading customers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen">

      {/* Error Alert */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-red-700">{error}</p>
          </div>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <button
          onClick={() => setIsImportModalOpen(true)}
          className="btn-secondary flex items-center justify-center gap-2"
        >
          <Upload size={18} />
          <span>Import CSV</span>
        </button>
        <button
          onClick={handleExportPdf}
          disabled={exporting || loading}
          className="bg-green-600 hover:bg-green-700 disabled:bg-green-300 disabled:cursor-not-allowed text-white flex items-center justify-center gap-2 px-4 py-2 rounded-lg"
        >
          {exporting ? <Loader size={18} className="animate-spin" /> : <Download size={18} />}
          <span>{exporting ? 'Exporting...' : 'Export PDF'}</span>
        </button>
        <button
          onClick={() => setIsAddModalOpen(true)}
          className="btn-primary flex items-center justify-center gap-2"
        >
          <Plus size={18} />
          <span>Add Customer</span>
        </button>
        <button
          onClick={() => setIsInstallModalOpen(true)}
          className="bg-orange-600 hover:bg-orange-700 text-white flex items-center justify-center gap-2 px-4 py-2 rounded-lg"
        >
          <Plus size={18} />
          <span>New Install</span>
        </button>
      </div>

      {/* Search & Filter Section */}
      <div className="card p-4 mb-6">
        {/* Search Bar */}
        <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-100">
          <Search className="w-5 h-5 text-gray-400 flex-shrink-0" />
          <input
            type="text"
            placeholder="Search by username, name, or phone..."
            className="flex-1 outline-none text-gray-700 bg-transparent"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} className="text-gray-400 hover:text-gray-600">
              <X size={18} />
            </button>
          )}
        </div>

        {/* Filter Dropdowns */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({...filters, status: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
              <option value="suspended">Suspended</option>
              <option value="churned">Churned</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Package</label>
            {fetchingFilters ? (
              <div className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50 text-gray-500 flex items-center gap-2">
                <Loader size={14} className="animate-spin" /> Loading...
              </div>
            ) : (
              <select
                value={filters.package}
                onChange={(e) => setFilters({...filters, package: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              >
                <option value="">All Packages</option>
                {allPackages.map(pkg => (
                  <option key={pkg.id} value={pkg.id}>
                    {pkg.name} {pkg.bandwidth ? `- ${pkg.bandwidth}` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Area</label>
            {fetchingFilters ? (
              <div className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50 text-gray-500 flex items-center gap-2">
                <Loader size={14} className="animate-spin" /> Loading...
              </div>
            ) : (
              <select
                value={filters.area}
                onChange={(e) => setFilters({...filters, area: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              >
                <option value="">All Areas</option>
                {allAreas.map(area => (
                  <option key={area.id} value={area.id}>{area.name}</option>
                ))}
              </select>
            )}
          </div>

          <div className="flex items-end">
            <button
              onClick={clearFilters}
              disabled={!hasActiveFilters}
              className={`w-full px-4 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                hasActiveFilters
                  ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  : 'bg-gray-50 text-gray-400 cursor-not-allowed'
              }`}
            >
              <RefreshCw size={16} />
              Clear Filters
            </button>
          </div>
        </div>

        {/* Active Filter Badges */}
        {hasActiveFilters && (
          <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap gap-2">
            {searchTerm && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                Search: "{searchTerm}"
                <button onClick={() => setSearchTerm('')} className="hover:text-blue-900"><X size={12} /></button>
              </span>
            )}
            {filters.status && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-medium">
                Status: {filters.status}
                <button onClick={() => setFilters({...filters, status: ''})} className="hover:text-purple-900"><X size={12} /></button>
              </span>
            )}
            {filters.package && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-50 text-green-700 rounded-full text-xs font-medium">
                Package: {allPackages.find(p => p.id === parseInt(filters.package))?.name || 'Unknown'}
                <button onClick={() => setFilters({...filters, package: ''})} className="hover:text-green-900"><X size={12} /></button>
              </span>
            )}
            {filters.area && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-orange-50 text-orange-700 rounded-full text-xs font-medium">
                Area: {allAreas.find(a => a.id === parseInt(filters.area))?.name || 'Unknown'}
                <button onClick={() => setFilters({...filters, area: ''})} className="hover:text-orange-900"><X size={12} /></button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Results Count + Pagination Controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <div className="text-sm text-gray-600">
          {totalCount > 0 ? (
            <>Showing <span className="font-semibold">{startItem}</span>-<span className="font-semibold">{endItem}</span> of <span className="font-semibold">{totalCount.toLocaleString()}</span> customers</>
          ) : (
            <span>No customers found</span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* Page Size Selector */}
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500">Per page:</span>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="px-2 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
            >
              {PAGE_SIZE_OPTIONS.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          {/* Page Navigation */}
          {totalPages > 1 && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => goToPage(1)}
                disabled={currentPage === 1}
                className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed text-gray-600"
              >
                <ChevronsLeft size={16} />
              </button>
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed text-gray-600"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="px-3 py-1 text-sm text-gray-700 font-medium">
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed text-gray-600"
              >
                <ChevronRight size={16} />
              </button>
              <button
                onClick={() => goToPage(totalPages)}
                disabled={currentPage === totalPages}
                className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed text-gray-600"
              >
                <ChevronsRight size={16} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Loading overlay for page transitions */}
      <div className="relative">
        {loading && customers.length > 0 && (
          <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center">
            <Loader className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
        )}

      {/* Desktop Table */}
      <div className="hidden lg:block card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">User</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Package</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Expiry/Status</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Financials</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Monthly</th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {customers.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    <p className="font-medium mb-1">No customers found</p>
                    <p className="text-sm">Try adjusting your search or filters</p>
                    {hasActiveFilters && (
                      <button onClick={clearFilters} className="mt-2 text-blue-600 hover:text-blue-700 text-sm font-medium">
                        Clear all filters
                      </button>
                    )}
                  </td>
                </tr>
              ) : (
                customers.map((customer) => (
                  <tr key={customer.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="font-semibold text-gray-900">{customer.username}</div>
                      <div className="text-xs text-gray-600">{customer.name}</div>
                      <div className="text-xs text-gray-600">{customer.phone}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-blue-600">{customer.package_name || 'N/A'}</div>
                      <div className="text-xs text-gray-600">{customer.area_name || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-xs text-gray-600 mb-2">
                        Exp: {customer.expiry_date ? new Date(customer.expiry_date).toLocaleDateString() : 'N/A'}
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                          customer.status === 'active' ? 'badge-success' :
                          customer.status === 'pending' ? 'bg-blue-100 text-blue-700' :
                          customer.status === 'expired' ? 'badge-warning' :
                          customer.status === 'suspended' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300' :
                          customer.status === 'churned' ? 'bg-red-100 text-red-700' :
                          'badge-danger'
                        }`}>
                          {customer.status.toUpperCase()}
                        </span>
                        {customer.is_new && (
                          <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold bg-teal-100 text-teal-700">
                            NEW
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {Number(customer.due_balance) > 0 && (
                        <div className="text-xs text-red-600 font-bold">Due: {customer.due_balance}</div>
                      )}
                      {Number(customer.credit_balance) > 0 && (
                        <div className="text-xs text-green-600 font-bold">Cr: {customer.credit_balance}</div>
                      )}
                      {Number(customer.due_balance) === 0 && Number(customer.credit_balance) === 0 && (
                        <div className="text-xs text-gray-400">—</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        PKR {customer.effective_price?.toFixed(2) || customer.package_monthly_price}
                      </div>
                      {customer.discount_type !== 'none' && (
                        <span className="text-xs text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full">
                          {customer.discount_type.replace('_', ' ')}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-2">
                        {customer.has_installation_invoice && (
                          <button
                            onClick={() => handleViewInvoice(customer.id)}
                            className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                            title="View Invoice"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                              <polyline points="14 2 14 8 20 8"></polyline>
                              <line x1="16" y1="13" x2="8" y2="13"></line>
                              <line x1="16" y1="17" x2="8" y2="17"></line>
                              <polyline points="10 9 9 9 8 9"></polyline>
                            </svg>
                          </button>
                        )}
                        <button
                          onClick={() => navigate(`/customers/${customer.id}/history`)}
                          className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                          title="View History"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => setEditingCustomer(customer)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(customer.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tablet/Mobile Card View */}
      <div className="lg:hidden space-y-4">
        {customers.length === 0 ? (
          <div className="card p-12 text-center">
            <p className="font-medium text-gray-900 mb-1">No customers found</p>
            <p className="text-sm text-gray-600">Try adjusting your search or filters</p>
            {hasActiveFilters && (
              <button onClick={clearFilters} className="mt-2 text-blue-600 hover:text-blue-700 text-sm font-medium">
                Clear all filters
              </button>
            )}
          </div>
        ) : (
          customers.map((customer) => (
            <div key={customer.id} className="card p-4 hover:shadow-md transition-all">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">{customer.username}</p>
                  <p className="text-xs text-gray-600">{customer.name}</p>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                    customer.status === 'active' ? 'badge-success' :
                    customer.status === 'pending' ? 'bg-blue-100 text-blue-700' :
                    customer.status === 'expired' ? 'badge-warning' :
                    customer.status === 'suspended' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300' :
                    customer.status === 'churned' ? 'bg-red-100 text-red-700' :
                    'badge-danger'
                  }`}>
                    {customer.status.toUpperCase()}
                  </span>
                  {customer.is_new && (
                    <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold bg-teal-100 text-teal-700">
                      NEW
                    </span>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-xs font-medium text-gray-600 mb-1">Phone</p>
                  <p className="text-sm text-gray-900">{customer.phone}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-600 mb-1">Package</p>
                  <p className="text-sm text-gray-900">{customer.package_name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-600 mb-1">Area</p>
                  <p className="text-sm text-gray-900">{customer.area_name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-600 mb-1">Expiry</p>
                  <p className="text-sm text-gray-900">{customer.expiry_date ? new Date(customer.expiry_date).toLocaleDateString() : 'N/A'}</p>
                </div>
              </div>
              {(Number(customer.due_balance) > 0 || Number(customer.credit_balance) > 0) && (
                <div className="bg-gray-50 px-3 py-2 rounded-lg mb-4 flex gap-4">
                  {Number(customer.due_balance) > 0 && (
                    <div>
                      <p className="text-xs font-medium text-gray-600">Due</p>
                      <p className="text-sm font-bold text-red-600">{customer.due_balance}</p>
                    </div>
                  )}
                  {Number(customer.credit_balance) > 0 && (
                    <div>
                      <p className="text-xs font-medium text-gray-600">Credit</p>
                      <p className="text-sm font-bold text-green-600">{customer.credit_balance}</p>
                    </div>
                  )}
                </div>
              )}
              <div className="flex gap-2">
                {customer.has_installation_invoice && (
                  <button
                    onClick={() => handleViewInvoice(customer.id)}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 transition-colors text-sm font-medium"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                    </svg>
                    <span>Invoice</span>
                  </button>
                )}
                <button
                  onClick={() => navigate(`/customers/${customer.id}/history`)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gray-50 text-gray-600 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium"
                >
                  <Eye size={16} />
                  <span>History</span>
                </button>
                <button
                  onClick={() => setEditingCustomer(customer)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium"
                >
                  <Edit2 size={16} />
                  <span>Edit</span>
                </button>
                <button
                  onClick={() => setDeleteConfirm(customer.id)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors text-sm font-medium"
                >
                  <Trash2 size={16} />
                  <span>Delete</span>
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      </div> {/* end relative wrapper */}

      {/* Bottom Pagination (mobile) */}
      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft size={16} />
            Prev
          </button>
          <span className="text-sm text-gray-600">
            Page <span className="font-semibold">{currentPage}</span> of <span className="font-semibold">{totalPages}</span>
          </span>
          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            Next
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:w-full max-w-sm p-6 animation-in">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Customer?</h3>
            <p className="text-sm text-gray-600 mb-6">This action cannot be undone. This will permanently delete the customer record.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="btn-secondary flex-1">Cancel</button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {editingCustomer && (
        <EditCustomerModal
          customer={editingCustomer}
          onClose={() => setEditingCustomer(null)}
          onSave={() => fetchCustomers(currentPage)}
        />
      )}

      {isAddModalOpen && (
        <AddCustomerModal
          onClose={() => setIsAddModalOpen(false)}
          onSave={() => fetchCustomers(currentPage)}
        />
      )}

      {isInstallModalOpen && (
        <NewInstallationModal
          onClose={() => setIsInstallModalOpen(false)}
          onSave={() => fetchCustomers(currentPage)}
        />
      )}

      {viewingInvoice && (
        <InstallationInvoiceView
          invoice={viewingInvoice}
          onClose={() => setViewingInvoice(null)}
          onEdit={(inv) => setEditingInvoice(inv)}
        />
      )}

      {editingInvoice && (
        <EditInstallationInvoiceModal
          invoice={editingInvoice}
          onClose={() => setEditingInvoice(null)}
          onSave={(updated) => {
            setViewingInvoice(updated);
            setEditingInvoice(null);
          }}
        />
      )}

      {isImportModalOpen && (
        <ImportCustomersModal
          onClose={() => setIsImportModalOpen(false)}
          onSuccess={() => fetchCustomers(currentPage)}
        />
      )}
    </div>
  );
};

export default Customers;
